import MapIcon from "@heroicons/react/24/solid/MapIcon";
import SwatchIcon from "@heroicons/react/24/solid/SwatchIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import apiFetch from "@/lib/fetch";
import { cn } from "@/lib/utils";
import Chips from "@/components/ui/chips";
import DropdownInput from "@/components/views/account/settings/interests/dropdown/DropdownInput";
import Dropdown from "@/components/views/account/settings/interests/dropdown/Dropdown";
import { useTranslation } from "@/hooks/translationContext";

const MAX_SELECTED_DISTRICT = 3;
const MAX_SELECTED_SERVICE = 5;

interface Props {
  setSelectedDistricts: (data: any) => void;
  setSelectedServices: (data: any) => void;
  selectedDistricts: any[];
  selectedServices: any[];
}

export default function InterestScreen(props: Props) {
  const { t } = useTranslation();
  const {
    setSelectedDistricts,
    selectedDistricts,
    setSelectedServices,
    selectedServices,
  } = props;

  const [serviceQuery, setServiceQuery] = useState("");
  const [districtQuery, setDistrictQuery] = useState("");

  const [debouncedServiceQuery] = useDebounce(serviceQuery, 500);
  const [debouncedDistrictQuery] = useDebounce(districtQuery, 500);
  const [services, setServices] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [serviceInputIsFocused, setServiceInputIsFocused] = useState(false);
  const [districtInputIsFocused, setDistrictInputIsFocused] = useState(false);

  const districtContainer = useRef<HTMLDivElement>(null);
  const serviceContainer = useRef<HTMLDivElement>(null);

  const mappedData = (data: any) => {
    return data.map((data: any) => ({
      id: data.id,
      title: data.title || data.descriptions[0]?.title,
    }));
  };

  const getAllServices = async (currentPage = 0) => {
    const res = await apiFetch({ route: `service/all?index=${currentPage}` });
    setServices(mappedData(res.data));
  };

  const getAllDistricts = async (currentPage = 0) => {
    const res = await apiFetch({ route: `district/all?index=${currentPage}` });
    setDistricts(mappedData(res.data));
  };

  const fetchServiceData = async (page: number) => {
    const res = await apiFetch({
      route: `service/search?q=${debouncedServiceQuery}`,
    });
    setServices(mappedData(res.data));
  };

  const fetchDistrictData = async (page: number) => {
    const res = await apiFetch({
      route: `district/search?q=${debouncedDistrictQuery}&index=${page}`,
    });
    setDistricts(mappedData(res.data));
  };

  const nextServiceData = (currentPage: number) => {
    if (debouncedServiceQuery.length > 0) {
      fetchServiceData(currentPage);
      return;
    }

    getAllServices(currentPage);
  };

  const nextDistrictData = (currentPage: number) => {
    if (debouncedDistrictQuery.length > 0) {
      fetchDistrictData(currentPage);
      return;
    }

    getAllDistricts(currentPage);
  };

  useEffect(() => {
    if (!debouncedServiceQuery.length) {
      getAllServices(0);
    }
    if (!debouncedDistrictQuery.length) {
      getAllDistricts(0);
    }
  }, [debouncedDistrictQuery, debouncedServiceQuery]);

  useEffect(() => {
    if (serviceInputIsFocused && !!debouncedServiceQuery) {
      fetchServiceData(0);
    }

    if (districtInputIsFocused && !!debouncedDistrictQuery) {
      fetchDistrictData(0);
    }
  }, [
    serviceInputIsFocused,
    debouncedServiceQuery,
    districtInputIsFocused,
    debouncedDistrictQuery,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        districtContainer.current &&
        !districtContainer.current.contains(event.target)
      ) {
        setDistrictInputIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [districtContainer]);

  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    window.addEventListener("resize", () => {
      setWidth(window.innerWidth);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        serviceContainer.current &&
        !serviceContainer.current.contains(event.target)
      ) {
        setServiceInputIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [serviceContainer]);

  return (
    <>
      <div className="w-full flex-col gap-6 max-h-[500px] justify-center items-center md:h-[calc(100vh-480px)] md:!min-h-[320px] ">
        <div className="flex flex-col gap-6 items-center justify-center h-full">
          <div className="flex flex-col items-start gap-[40px] relative w-full left-0">
            <div
              className={
                districtInputIsFocused
                  ? "max-md:h-full w-full max-md:fixed max-md:left-0 max-md:top-0 max-md:bg-white max-md:z-50"
                  : "w-full"
              }
            >
              {districtInputIsFocused && (
                <XMarkIcon className="md:hidden fixed top-4 right-4 z-[99] size-8 text-assistant cursor-pointer" />
              )}
              <div
                ref={districtContainer}
                className={`flex flex-col gap-2 w-full ${districtInputIsFocused ? "md:h-auto md:z-50 md:justify-start" : serviceInputIsFocused ? "md:opacity-0 md:duration-500" : "md:duration-500"}`}
              >
                <div
                  className={cn(
                    "w-full bg-white flex-col justify-start items-start inline-flex",
                    districtInputIsFocused
                      ? "max-md:translate-y-24 max-md:duration-300 max-md:px-6 md:h-72 md:z-50 md:border md:border-slate-300 md:bg-white md:rounded-[20px]"
                      : "md:h-auto",
                  )}
                >
                  <DropdownInput
                    query={districtQuery}
                    setQuery={setDistrictQuery}
                    selectedData={selectedDistricts}
                    inputIsFocused={districtInputIsFocused}
                    setInputIsFocused={setDistrictInputIsFocused}
                    placeholder={t("account-interests-district-placeholder")}
                    icon={MapIcon}
                  />

                  {districtInputIsFocused && (
                    <Dropdown
                      height={220}
                      multipleSelect={true}
                      data={districts}
                      loading={
                        selectedDistricts.length >= MAX_SELECTED_DISTRICT
                      }
                      nextData={nextDistrictData}
                      hasMore={false}
                      selectedData={selectedDistricts}
                      setSelectedData={(data: any) => {
                        setSelectedDistricts((prev: any) => [...prev, data]);
                      }}
                      onClose={() => {
                        setDistrictInputIsFocused(false);
                      }}
                    />
                  )}
                </div>

                {!serviceInputIsFocused && (
                  <div
                    className={
                      districtInputIsFocused
                        ? "max-md:translate-y-24 max-md:px-6"
                        : ""
                    }
                  >
                    <Chips
                      chips={selectedDistricts}
                      setSelectedChips={setSelectedDistricts}
                    />
                  </div>
                )}
              </div>
            </div>
            <div
              className={
                serviceInputIsFocused
                  ? "max-md:h-full w-full max-md:fixed max-md:left-0 max-md:top-0 max-md:bg-white max-md:z-50"
                  : "w-full"
              }
            >
              {serviceInputIsFocused && (
                <XMarkIcon className="md:hidden fixed top-4 right-4 z-[99] size-8 text-assistant cursor-pointer" />
              )}

              <div
                ref={serviceContainer}
                className={cn(
                  "flex flex-col gap-2 md:z-50 justify-start w-full",
                  serviceInputIsFocused && "md:h-[106px]",
                )}
              >
                <div
                  className={cn(
                    "w-full bg-white flex-col justify-start items-start inline-flex",
                    serviceInputIsFocused
                      ? "max-md:translate-y-24 max-md:duration-300 max-md:px-6 md:h-72 md:z-50 md:border md:-translate-y-[25vh] md:duration-500 md:border-slate-300 md:bg-white md:rounded-[20px]"
                      : districtInputIsFocused
                        ? "md:duration-500 md:-translate-y-[45px] md:transition-opacity md:opacity-0"
                        : "max-md:duration-300 md:h-auto md:duration-500",
                  )}
                >
                  <DropdownInput
                    query={serviceQuery}
                    setQuery={setServiceQuery}
                    selectedData={selectedServices}
                    inputIsFocused={serviceInputIsFocused}
                    setInputIsFocused={setServiceInputIsFocused}
                    placeholder={t("quote-page-interests-service-placeholder")}
                    icon={SwatchIcon}
                  />

                  {serviceInputIsFocused && (
                    <Dropdown
                      height={220}
                      multipleSelect={true}
                      data={services}
                      loading={selectedServices.length >= MAX_SELECTED_SERVICE}
                      nextData={nextServiceData}
                      hasMore={false}
                      selectedData={selectedServices}
                      setSelectedData={(data: any) => {
                        setSelectedServices((prev: any) => [...prev, data]);
                      }}
                      onClose={() => {
                        setServiceInputIsFocused(false);
                      }}
                    />
                  )}
                </div>

                <div
                  className={`${serviceInputIsFocused && "md:-translate-y-[200px] max-md:translate-y-24 max-md:px-6 z-[9999]"}`}
                >
                  {!districtInputIsFocused && (
                    <Chips
                      chips={selectedServices}
                      setSelectedChips={setSelectedServices}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className={
              districtInputIsFocused || serviceInputIsFocused
                ? "absolute bottom-0 w-full rounded-full h-[62px] bg-white z-50 md:duration-200 hidden md:flex"
                : "md:duration-200"
            }
          ></div>
        </div>
      </div>
    </>
  );
}
